import { isEmpty } from 'lodash-es';

export function appendObjectToQueryString(path: string, object: object): string {
  if (!object || isEmpty(object)) {
    return path;
  }

  const appendedParams = Object.keys(object)
    .map(key => {
      const encodedKey = encodeURIComponent(key);
      const value = object[key];
      const processedValue = typeof value === 'object' ? JSON.stringify(value) : value;
      const encodedValue = encodeURIComponent(processedValue);
      return `${encodedKey}=${encodedValue}`;
    })
    .join('&');
  const concatChar = path.includes('?') ? '&' : '?';
  return `${path}${concatChar}${appendedParams}`;
}

function parseStringValue(value: string) {
  let result: number | object | string | boolean;
  try {
    // Numbers with more than 16 chars, possibly are codes (like in loyalty) so we don't parse them
    result = value.length >= 16 && !isNaN(value as any) ? value : JSON.parse(value);
  } catch (error) {
    result = value;
  }
  return result;
}

export function convertQueryStringToObject<T extends object>(queryString: String): T {
  const params = new URLSearchParams(queryString.replace('?', ''));

  const result = {} as T;
  params.forEach((value: string, key: string) => {
    const normalizedKey = key.replace('[]', '');
    const parsedValue = parseStringValue(value);

    const previousValue = result[normalizedKey];
    if (previousValue) {
      // Handle array like parameters like ?someParam[]=123&someParam[]=text
      result[normalizedKey] = [
        Array.isArray(previousValue) ? [...previousValue] : previousValue,
        parsedValue,
      ];
    } else {
      result[normalizedKey] = parsedValue;
    }
  });
  return result;
}

/**
 * This method is used to extract query parameters from a URL string or query string.
 * This method is different from the convertQueryStringToObject method because it handles different input formats.
 * 1. Full URL: "https://example.com?foo=bar&baz=123"
 * 2. Query string with ?: "?foo=bar&baz=123"
 * 3. Query string without ?: "foo=bar&baz=123"
 * @param input - URL string or query string (e.g., "https://example.com?foo=bar&baz=123" or "?foo=bar&baz=123")
 * @returns Object with parameter names as keys and values as strings
 *
 * @example
 * extractQueryParams("https://example.com?foo=bar&baz=123&empty=")
 * // Returns: { foo: "bar", baz: "123", empty: "" }
 *
 * @example
 * extractQueryParams("?utm_source=google&utm_medium=cpc")
 * // Returns: { utm_source: "google", utm_medium: "cpc" }
 */
export const extractQueryParams = (input: string): Record<string, string> => {
  if (!input || typeof input !== 'string') {
    return {};
  }

  try {
    // Handle different input formats:
    // 1. Full URL: "https://example.com?foo=bar&baz=123"
    // 2. Query string with ?: "?foo=bar&baz=123"
    // 3. Query string without ?: "foo=bar&baz=123"
    let queryString = input;

    if (input.includes('?')) {
      // Full URL or query string with ? - take everything after ?
      queryString = input.split('?')[1];
    }
    // If no ?, assume it's already a query string without the ? prefix

    if (!queryString) {
      return {};
    }

    const params: Record<string, string> = {};
    const urlParams = new URLSearchParams(queryString);

    urlParams.forEach((value, key) => {
      params[key] = value;
    });

    return params;
  } catch (error) {
    // Silently fail for query parameter extraction
    return {};
  }
};
